import Box from 'ext-ui/components/Box/Box'
import Pill from 'ext-ui/components/Pill/Pill'
import React from 'react'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import { useTranslate } from 'src/localization/Trns'
import * as style from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsTab.style'
import StatementList from 'src/page-components/Accounts/AccountStatementsTab/components/StatementList/StatementList'
import StatementPayments from 'src/page-components/Accounts/AccountStatementsTab/components/StatementPayments/StatementPayments'
import { useAccountStatementsData } from 'src/page-components/Accounts/AccountStatementsTab/useAccountStatementsData'
import {
  cardDetailTabSectionHeading,
  cardDetailTabSectionTitle,
} from 'src/page-components/Accounts/cardDetailTabs.style'

interface AccountStatementsTabProps {
  /** The card whose statements to read. */
  creditCardId: string | undefined
  /** Card label for the statement-detail modal, e.g. "T&E Card •• 5678". */
  cardLabel: string
}

/**
 * Statements tab content for the card-detail page (EX-40600).
 *
 * Phase 1 is view-only by design. Payments precedes Statements, the current
 * statement is the first row, and there is no Pay action anywhere — Extend has
 * no bank-payment capability, so that absence is the contract, not a gap. The
 * per-statement Download control is Phase 2 and deliberately not built here.
 *
 * DATA IS FABRICATED. All of it arrives through `useAccountStatementsData`, the
 * one seam a real endpoint replaces — read the warning there before enabling
 * this tab anywhere a customer can reach it.
 *
 * Routing stays in `AccountEntityPage` — this is props-only tab content.
 */
const AccountStatementsTab: React.FC<AccountStatementsTabProps> = ({
  creditCardId,
  cardLabel,
}) => {
  const translate = useTranslate()
  // Destructured by group, so a group that later grows loading/error state
  // changes only its own section.
  const { payments, statements } = useAccountStatementsData(creditCardId)

  // Nothing else on this page distinguishes fabricated figures from real ones,
  // and the frozen demo dates drift further from today the longer the endpoint
  // takes — an OPEN statement with a past due date reads as a genuine overdue
  // bill. Delete this at the same time as the demo constant, not before.
  const sampleDataBadge = (
    <Pill color="pending" size="small">
      {translate('Sample data')}
    </Pill>
  )

  return (
    // Every sibling tab supplies its own BackgroundPageContent — that is where
    // the page gutters live, so without it this content renders flush to the
    // viewport edges.
    <BackgroundPageContent>
      {/*
        BackgroundPageContent renders only `className` — it does NOT spread the
        rest of its props — so the test id has to live on an element we own.
      */}
      <div css={style.sections} data-testid="accountStatementsTab-content">
        <Box>
          <div css={cardDetailTabSectionHeading}>
            <h2 css={cardDetailTabSectionTitle}>{translate('Payments')}</h2>
            {sampleDataBadge}
          </div>
          <StatementPayments
            nextPaymentAmount={payments.nextPaymentAmount}
            nextPaymentDueOn={payments.nextPaymentDueOn}
            payments={payments.history}
          />
        </Box>

        <Box>
          <div css={cardDetailTabSectionHeading}>
            <h2 css={cardDetailTabSectionTitle}>{translate('Statements')}</h2>
            {sampleDataBadge}
          </div>
          <StatementList
            recentStatements={statements.recent}
            olderStatements={statements.older}
            cardLabel={cardLabel}
          />
        </Box>
      </div>
    </BackgroundPageContent>
  )
}

export default AccountStatementsTab
